import { useLayoutEffect, useRef } from 'react'
import { MOBILE_BREAKPOINT } from './use-mobile'

/**
 * Peek-style auto-hide header hook.
 * Adapted from https://github.com/ADesignl/Peek to work with
 * custom scroll containers (Radix ScrollArea) and mobile-only activation.
 *
 * Manipulates the DOM directly via classList + requestAnimationFrame —
 * no React re-renders on scroll.
 */

interface PeekClasses {
  pinned: string
  unpinned: string
  top: string
  notTop: string
}

interface PeekOptions {
  offset?: number
  tolerance?: number
  classes: PeekClasses
}

/**
 * Walk up the DOM looking for a scroll container. Checks both computed
 * overflow styles AND the Radix ScrollArea viewport data attribute.
 *
 * Radix's ScrollAreaScrollbar enables overflow via useEffect, so the
 * viewport may momentarily have `overflow: hidden` before the scrollbar
 * effect fires. We accept a Radix viewport even if overflow isn't set yet
 * since we know it will become scrollable.
 */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement
  while (node) {
    // Radix viewport — will be scrollable once ScrollBar effect fires
    if (node.hasAttribute('data-radix-scroll-area-viewport')) {
      return node
    }
    const { overflow, overflowY } = getComputedStyle(node)
    if (
      overflow.includes('auto') ||
      overflow.includes('scroll') ||
      overflowY.includes('auto') ||
      overflowY.includes('scroll')
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

export function usePeek(options: PeekOptions) {
  const ref = useRef<HTMLDivElement>(null)
  const optionsRef = useRef(options)
  optionsRef.current = options

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const scrollParent = findScrollParent(el)
    const scrollElement =
      scrollParent ?? (document.scrollingElement as HTMLElement | null) ?? document.documentElement
    const scrollTarget: HTMLElement | Window = scrollParent ?? window
    if (!scrollElement) return

    const { tolerance = 5 } = optionsRef.current
    const cls = optionsRef.current.classes

    // The offset must be at least the header's own height so the header
    // only hides once the content has scrolled up to fill its flow space.
    const configOffset = optionsRef.current.offset ?? 0
    const offset = Math.max(configOffset, el.offsetHeight)

    let lastScrollY = scrollElement.scrollTop
    let isAtTop = true
    let isPinned = true
    let ticking = false

    // Publish two CSS variables for sticky elements that dock below the header:
    //   --page-header-height: the header's measured height (for layout spacers and
    //     grid sizing — always the real height, regardless of peek state).
    //   --page-header-offset: how far those elements should dock from the top —
    //     the header height while it's pinned, but 0 once the peek header hides.
    //     Without this, a sticky child (e.g. a kanban column header at
    //     `top: var(--page-header-offset)`) would stay pinned a full header-height
    //     below the top with nothing above it, leaving a gap where cards show above
    //     the heading as it cuts across them (ClickUp 86d3d2qtp).
    const publishDockVars = () => {
      const heightPx = `${el.offsetHeight}px`
      const offsetPx = isPinned ? heightPx : '0px'
      for (const target of [scrollElement, el.parentElement]) {
        if (!target) continue
        target.style.setProperty('--page-header-height', heightPx)
        target.style.setProperty('--page-header-offset', offsetPx)
      }
    }

    function update() {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        if (!isPinned) {
          isPinned = true
          el!.classList.add(cls.pinned)
          el!.classList.remove(cls.unpinned)
          publishDockVars()
        }
        ticking = false
        return
      }

      const scrollY = scrollElement.scrollTop
      const direction = scrollY > lastScrollY ? 'down' : 'up'
      const delta = Math.abs(scrollY - lastScrollY)

      if (scrollY <= offset) {
        if (!isAtTop) {
          isAtTop = true
          el!.classList.add(cls.top)
          el!.classList.remove(cls.notTop)
          el!.classList.add(cls.pinned)
          el!.classList.remove(cls.unpinned)
          isPinned = true
          publishDockVars()
        }
      } else {
        if (isAtTop) {
          isAtTop = false
          el!.classList.remove(cls.top)
          el!.classList.add(cls.notTop)
        }

        if (delta >= tolerance) {
          if (direction === 'down' && isPinned) {
            isPinned = false
            el!.classList.remove(cls.pinned)
            el!.classList.add(cls.unpinned)
            publishDockVars()
          } else if (direction === 'up' && !isPinned) {
            isPinned = true
            el!.classList.add(cls.pinned)
            el!.classList.remove(cls.unpinned)
            publishDockVars()
          }
        }
      }

      lastScrollY = scrollY
      ticking = false
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(update)
        ticking = true
      }
    }

    // Initial state
    el.classList.add(cls.top, cls.pinned)

    publishDockVars()
    const ro = new ResizeObserver(publishDockVars)
    ro.observe(el)

    scrollTarget.addEventListener('scroll', requestTick, { passive: true })

    return () => {
      ro.disconnect()
      scrollElement.style.removeProperty('--page-header-height')
      scrollElement.style.removeProperty('--page-header-offset')
      if (el.parentElement) {
        ;(el.parentElement as HTMLElement).style.removeProperty('--page-header-height')
        ;(el.parentElement as HTMLElement).style.removeProperty('--page-header-offset')
      }
      scrollTarget.removeEventListener('scroll', requestTick)
      el.classList.remove(cls.pinned, cls.unpinned, cls.top, cls.notTop)
    }
  }, [])

  return ref
}
